import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { ShortageRecoveryService } from './shortage-recovery.service';
import { AppConstants } from '../../../app.constants';

Chart.register(...registerables);

@Component({
  selector: 'app-shortage-recovery-panel',
  templateUrl: './shortage-recovery-panel.component.html',
  styleUrl: './shortage-recovery-panel.component.scss'
})
export class ShortageRecoveryPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  recoveryData: any[] = [];
  charts: { [key: string]: any } = {};
  isLoading: boolean = false;

  // NDC Details Modal variables
  showNdcDetailsModal: boolean = false;
  selectedNdcDetails: any = null;
  selectedNdcParent: any = null;

  chartRawData: any[] = [];

  constructor(private recoveryService: ShortageRecoveryService) { }

  ngOnInit(): void {
    this.fetchRecoveryData();
  }

  ngAfterViewInit(): void {
    // Chart will be initialized after data is fetched
  }

  ngOnDestroy(): void {
    Object.values(this.charts).forEach(chart => chart.destroy());
  }

  fetchRecoveryData(): void {
    this.isLoading = true;
    const email = localStorage.getItem('email') || "";
    this.recoveryService.getShortageRecovery(email).subscribe({
      next: (res) => {
        // Map sections object to array
        if (res.sections) {
          this.recoveryData = Object.keys(res.sections).map(key => ({
            key,
            records: res.sections[key],
            open: false
          }));
        }

        // Map chart data
        if (res.chart_data) {
          this.chartRawData = res.chart_data;
          setTimeout(() => this.initRecoveryChart(), 100);
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error("Error fetching recovery data", err);
        this.isLoading = false;
      }
    });
  }

  initRecoveryChart(): void {
    const labels = this.chartRawData.map(d => d.month);
    const counts = this.chartRawData.map(d => d.count);

    const recCtx = document.getElementById('recoveryChart') as HTMLCanvasElement;
    if (recCtx) {
      if (this.charts.recovery) this.charts.recovery.destroy();
      this.charts.recovery = new Chart(recCtx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [{
            label: "Expected Recovery Count",
            data: counts,
            backgroundColor: "rgba(155, 68, 99, 0.8)",
            borderColor: "#9B4463",
            borderWidth: 1,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { grid: { display: false }, beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
            x: { grid: { display: false }, ticks: { font: { size: 10 } } }
          },
        },
      });
    }
  }

  openNdcDetailsModal(ndcItem: any, parentItem: any, event: Event): void {
    event.stopPropagation();
    
    // Normalize properties directly to latest_record for flat/recovery format
    this.selectedNdcDetails = {
      ...ndcItem,
      latest_record: ndcItem.latest_record || {
        status: ndcItem.status,
        availability_information: ndcItem.availability_information,
        type_of_update: ndcItem.type_of_update,
        created_at: ndcItem.created_at,
        company_name: ndcItem.company_name,
        generic_name: ndcItem.generic_name,
        date_of_update: ndcItem.date_of_update,
        date_discontinued: ndcItem.date_discontinued,
        initial_posting_date: ndcItem.initial_posting_date,
        presentation: ndcItem.presentation,
        reason_for_shortage: ndcItem.reason_for_shortage,
        related_information: ndcItem.related_information,
        resolved_note: ndcItem.resolved_note,
        therapeutic_category: ndcItem.therapeutic_category,
        ndc: ndcItem.ndc
      }
    };
    
    this.selectedNdcParent = parentItem;
    this.showNdcDetailsModal = true;
  }

  closeNdcDetailsModal(): void {
    this.showNdcDetailsModal = false;
    this.selectedNdcDetails = null;
    this.selectedNdcParent = null;
  }

  getVal(fieldObj: any): any {
    if (!fieldObj) return 'N/A';
    if (typeof fieldObj === 'object' && 'value' in fieldObj) {
      return fieldObj.value !== null && fieldObj.value !== undefined ? fieldObj.value : 'N/A';
    }
    return fieldObj;
  }

  getFormattedDate(dateVal: any): string {
    const val = this.getVal(dateVal);
    if (!val || val === 'N/A') return 'N/A';

    // Try parsing the date
    const d = new Date(val);
    if (isNaN(d.getTime())) {
      return val; // Return original string if not a valid date
    }

    // Format to dd-MM-yyyy
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatArrayOrString(val: any): string {
    if (!val) return 'N/A';
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    return val;
  }
}
